//@flow
import {useSelector} from 'react-redux';

import {actions as bulkEditActions} from '../duck';
import {useActions} from '../../../utils/globalStore';

import type {ProductRequest} from '../../products/api';
import {useCallback, useState} from 'react';
import {uploadImageService} from '../../file-storage/promises';
import {useProductVariantRequestContext} from '../context/productVariantRequest';
import {useProductVariantContext} from '../context/productVariant';
import {useProductContext} from '../context';
import {changeProductImageService} from '../../products/promises';
import {extractInfoProductField} from './extractInfoProduct';

// import type {FilterType} from '../duck';
import type {RootState} from '../..';

const editProductAction = bulkEditActions.editProductField;
const editProductActionBulk = bulkEditActions.editProductFieldBulk;

export function useEditProduct(productId: string) {
  const editAction = useActions(editProductAction);
  const editActionBulk = useActions(editProductActionBulk);

  const editProduct = useCallback(
    (product: ProductRequest) => {
      editAction(product, productId);
    },
    [productId, editAction]
  );

  const editProductInBulkEdit = useCallback(
    (product: any) => {
      const {productData, variantData, type} = product;
      const {formatProduct} = extractInfoProductField(
        productData,
        variantData,
        type,
        productId
      );
      editActionBulk(formatProduct);
    },
    [productId, editActionBulk]
  );

  return {
    editProduct,
    editProductInBulkEdit,
  };
}

export function useProductImage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const [{images, productId}] = useProductContext(product => ({
    images: product.images || [],
    productId: product.id,
  }));

  const addProductImage = async (imageId: string) => {
    const prevImages = images ? images.map(image => image.id) : [];

    await changeProductImageService(productId, [...prevImages, imageId]);
  };

  const setProductImage = async (imagesId: string[]) => {
    setLoading(true);

    changeProductImageService(productId, imagesId)
      .catch(() => {
        setError(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const setPosterImage = async (image: string | File) => {
    const prevImages = images ? images.map(image => image.id) : [];
    try {
      if (typeof image === 'string') {
        const newList = prevImages.filter(id => id !== image);
        await setProductImage([image, ...newList]);
        return image;
      }

      const id = await addNewImage(image);
      if (id) setProductImage([id, ...prevImages]);
      return id;
    } catch (error) {
      setError(true);
    }
  };

  const addNewImage = async (file: File) => {
    setLoading(true);
    setError(false);
    try {
      const result = await uploadImageService(file, 'im/product');

      const [uploaded] = result;

      const {id} = uploaded;

      await addProductImage(id);

      return id;
    } catch (error) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return {
    addNewImage,
    loading,
    error,
    setProductImage,
    setPosterImage,
  };
}

export function useChangeVariantImage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const variant = useProductVariantContext();
  const [{images, productId}] = useProductContext(product => ({
    images: product.images,
    productId: product.id,
  }));
  const variantRequest = useProductVariantRequestContext();

  const addProductImage = async (imageId: string) => {
    const prevImages = images ? images.map(image => image.id) : [];

    await changeProductImageService(productId, [...prevImages, imageId]);
  };

  const addNewVariantImage = async (file: File) => {
    setLoading(true);
    setError(false);
    try {
      const result = await uploadImageService(file, 'im/product');

      const [uploaded] = result;

      const {id} = uploaded;

      variantRequest.setImageId(variant.id, id);

      await addProductImage(id);

      return id;
    } catch (error) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const changeVariantImage = (imageId: string) => {
    variantRequest.setImageId(variant.id, imageId);
  };

  return {
    addNewVariantImage,
    changeVariantImage,
    loading,
    error,
  };
}

export function useProductEditState() {
  const {success, error} = useSelector((state: RootState) => ({
    success: state.bulkEdit.successEditingProduct,
    error: state.bulkEdit.errorEditingProduct,
  }));

  return {success, error};
}
